import { useState } from "react";
import { FileDown, Send, Inbox, ShieldCheck, Link2, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

export const STATUS_TONE = {
  not_submitted: "bg-slate-100 text-slate-600",
  submitted: "bg-sky-100 text-sky-700",
  approved: "bg-emerald-100 text-emerald-700",
  approved_as_noted: "bg-teal-100 text-teal-700",
  revise_resubmit: "bg-violet-100 text-violet-700",
  rejected: "bg-rose-100 text-rose-700",
  for_record: "bg-amber-100 text-amber-800",
  closed: "bg-slate-200 text-slate-700",
};
const ACTIONS = [["co_draft", "CO draft"], ["rfi_sent", "RFI sent"], ["po_created", "PO created"],
  ["resubmitted", "Resubmitted"], ["no_action", "No action"]];
const d10 = (s) => (s || "").slice(0, 10) || "—";

/** SUB-1 §1 — one revision's record: package sent, returned copy, stamp read, status confirm /
 *  override, reviewer, action taken and links. The system prepares, a person confirms. */
export default function RevisionPanel({ project, rev, statuses, canEdit, onChanged, onLogReturn }) {
  const [busy, setBusy] = useState(false);
  const [override, setOverride] = useState("");
  const [reason, setReason] = useState("");
  const [actionKind, setActionKind] = useState("");
  const [actionReason, setActionReason] = useState("");
  const pending = rev.pending_return || {};
  const returned = rev.returned || {};
  const stamp = rev.stamp || {};
  const returnOutcomes = statuses.filter((s) => ["approved", "approved_as_noted", "revise_resubmit", "rejected", "for_record"].includes(s.value));

  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (ok) toast.success(ok); onChanged(r); }
    catch (e) { toast.error(e?.response?.data?.detail || "That could not be saved"); }
    finally { setBusy(false); }
  };
  const confirm = (status, why = "") => run(() => opsApi.submittalConfirmStatus(project.id, rev.id, { status, reason: why }),
    `${rev.number} → ${statuses.find((s) => s.value === status)?.label || status}`);
  const openDoc = (id) => opsApi.openDocument(id).catch(() => toast.error("The file could not be opened"));

  return (
    <div className="grid gap-3 md:grid-cols-3" data-testid={`ops-revision-panel-${rev.id}`}>
      {/* sent */}
      <section className="rounded-lg border border-slate-200 bg-white p-3 text-xs">
        <p className="flex items-center gap-1 font-semibold text-slate-700"><Send className="h-3.5 w-3.5" /> Package sent</p>
        {rev.submitted_at ? (
          <dl className="mt-2 space-y-1 text-slate-600">
            <div className="flex justify-between"><dt>Date</dt><dd>{d10(rev.submitted_at)}</dd></div>
            <div className="flex justify-between"><dt>Method</dt><dd>{rev.sent?.method || "—"}</dd></div>
            <div className="flex justify-between"><dt>To</dt><dd className="truncate pl-2">{(rev.sent?.to || []).join(", ") || "—"}</dd></div>
            <div className="flex justify-between"><dt>Due back</dt><dd>{d10(rev.due_back_at)}</dd></div>
            <div className="flex justify-between"><dt>Ball in court</dt><dd className="font-medium uppercase" data-testid="ops-revision-ball">{rev.ball_in_court}</dd></div>
          </dl>
        ) : <p className="mt-2 text-slate-500">Not sent yet — Draft.</p>}
        {rev.pdf_ready && (
          <button onClick={() => opsApi.submittalPdfDownload(project.id, rev.id, `${rev.number}.pdf`)}
                  className="mt-2 inline-flex items-center gap-1 text-sky-700 hover:underline" data-testid="ops-revision-package-pdf">
            <FileDown className="h-3.5 w-3.5" /> package PDF
          </button>
        )}
      </section>

      {/* returned */}
      <section className="rounded-lg border border-slate-200 bg-white p-3 text-xs">
        <p className="flex items-center gap-1 font-semibold text-slate-700"><Inbox className="h-3.5 w-3.5" /> Returned copy</p>
        {pending.intake_id && (
          <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900" data-testid="ops-revision-pending">
            <p className="font-medium">Pending confirm — arrived by email ({pending.matched_on})</p>
            <p className="mt-0.5 truncate">{pending.subject}</p>
            <p className="text-[11px] text-amber-700">{pending.from} · {d10(pending.received_at)}</p>
            {pending.document_id && (
              <button onClick={() => openDoc(pending.document_id)} className="mt-1 inline-flex items-center gap-1 text-sky-700 hover:underline">
                <FileDown className="h-3 w-3" /> {pending.file_name || "open the file"}
              </button>
            )}
            {canEdit && (
              <div className="mt-2 flex gap-1.5">
                <Button size="sm" variant="data" className="h-7 text-xs" disabled={busy} data-testid="ops-revision-pending-confirm"
                        onClick={() => run(() => opsApi.submittalPendingConfirm(project.id, rev.id, {}), "Return confirmed")}>
                  <Check className="mr-1 h-3 w-3" /> Confirm
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} data-testid="ops-revision-pending-dismiss"
                        onClick={() => run(() => opsApi.submittalPendingDismiss(project.id, rev.id), "Sent back to Unmatched returns")}>
                  <X className="mr-1 h-3 w-3" /> Not this one
                </Button>
              </div>
            )}
          </div>
        )}
        {returned.document_id || rev.returned_document_id ? (
          <dl className="mt-2 space-y-1 text-slate-600">
            <div className="flex justify-between"><dt>Received</dt><dd>{d10(returned.at || rev.returned_at)}</dd></div>
            <div className="flex justify-between"><dt>From</dt><dd className="truncate pl-2">{returned.from || "—"}</dd></div>
            <div className="flex justify-between"><dt>Method</dt><dd>{returned.method || "—"}</dd></div>
            <div className="flex justify-between"><dt>Reviewer</dt><dd>{rev.reviewer || "—"}</dd></div>
            <div className="flex justify-between"><dt>Days out</dt><dd>{rev.days_out ?? "—"}</dd></div>
          </dl>
        ) : !pending.intake_id && <p className="mt-2 text-slate-500">{rev.submitted_at ? `Out ${rev.days_out ?? 0} day(s) — nothing back yet.` : "—"}</p>}
        <div className="mt-2 flex flex-wrap gap-2">
          {(returned.document_id || rev.returned_document_id) && (
            <button onClick={() => openDoc(returned.document_id || rev.returned_document_id)} data-testid="ops-revision-returned-open"
                    className="inline-flex items-center gap-1 rounded-full bg-[#007BFF] px-2.5 py-1 text-white hover:bg-[#0069d9]">
              <FileDown className="h-3.5 w-3.5" /> Open returned copy
            </button>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onLogReturn(rev)} data-testid="ops-revision-log-return">
              Log return
            </Button>
          )}
        </div>
        {rev.comments && <p className="mt-2 rounded-md bg-slate-50 p-2 text-slate-700"><span className="font-medium">Reviewer comments:</span> {rev.comments}</p>}
      </section>

      {/* status · action · links */}
      <section className="rounded-lg border border-slate-200 bg-white p-3 text-xs">
        <p className="flex items-center gap-1 font-semibold text-slate-700"><ShieldCheck className="h-3.5 w-3.5" /> Stamp status</p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_TONE[rev.status]}`} data-testid="ops-revision-status">{rev.status_label}</span>
          {stamp.source === "stamp_match" && <span className="rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700" title={`matched “${stamp.phrase}”`}>stamp match</span>}
          {stamp.override_reason && <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800" title={stamp.override_reason}>override</span>}
        </div>
        {stamp.phrase && <p className="mt-1 text-slate-500">Stamp read: “{stamp.phrase}”</p>}

        {rev.proposed_status && (
          <div className="mt-2 rounded-md border border-sky-200 bg-sky-50 p-2" data-testid="ops-revision-proposed">
            <p className="text-sky-900">Proposed: <span className="font-semibold">{rev.proposed_status_label}</span> — confirm or override.</p>
            {canEdit && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Button size="sm" variant="data" className="h-7 text-xs" disabled={busy} onClick={() => confirm(rev.proposed_status)} data-testid="ops-revision-confirm">
                  <Check className="mr-1 h-3 w-3" /> Confirm
                </Button>
                <select value={override} onChange={(e) => setOverride(e.target.value)} className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-xs" data-testid="ops-revision-override-status">
                  <option value="">Override to…</option>
                  {returnOutcomes.filter((s) => s.value !== rev.proposed_status).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            )}
          </div>
        )}
        {canEdit && !rev.proposed_status && rev.status === "submitted" && (returned.document_id || rev.returned_document_id) && (
          <div className="mt-2">
            <p className="text-slate-600">The stamp could not be read — set the status (reason required):</p>
            <select value={override} onChange={(e) => setOverride(e.target.value)} className="mt-1 h-7 w-full rounded-md border border-slate-200 bg-white px-1.5 text-xs" data-testid="ops-revision-set-status">
              <option value="">Set status…</option>
              {returnOutcomes.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        )}
        {override && (
          <div className="mt-1.5 flex gap-1.5" data-testid="ops-revision-override-row">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (logged)" className="h-7 text-xs" data-testid="ops-revision-override-reason" />
            <Button size="sm" variant="data" className="h-7 text-xs" disabled={busy || !reason.trim()} data-testid="ops-revision-override-apply"
                    onClick={() => { confirm(override, reason); setOverride(""); setReason(""); }}>Apply</Button>
          </div>
        )}

        <p className="mt-3 font-semibold text-slate-700">Action taken</p>
        {rev.action?.kind ? (
          <p className="mt-1 text-slate-600" data-testid="ops-revision-action">{rev.action.label}{rev.action.reason ? ` — ${rev.action.reason}` : ""}
            <span className="text-slate-400"> · {d10(rev.action.at)}{rev.action.days_to_action != null ? ` · ${rev.action.days_to_action}d after return` : ""}</span></p>
        ) : <p className="mt-1 text-slate-500">—</p>}
        {canEdit && (returned.document_id || rev.returned_document_id) && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <select value={actionKind} onChange={(e) => setActionKind(e.target.value)} className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-xs" data-testid="ops-revision-action-kind">
              <option value="">Record action…</option>
              {ACTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {actionKind && (
              <>
                <Input value={actionReason} onChange={(e) => setActionReason(e.target.value)} placeholder={actionKind === "no_action" ? "Reason (required)" : "Note"} className="h-7 w-40 text-xs" data-testid="ops-revision-action-reason" />
                <Button size="sm" variant="data" className="h-7 text-xs" disabled={busy || (actionKind === "no_action" && !actionReason.trim())} data-testid="ops-revision-action-apply"
                        onClick={() => run(() => opsApi.submittalAction(project.id, rev.id, { kind: actionKind, reason: actionReason }), "Action recorded").then(() => { setActionKind(""); setActionReason(""); })}>
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                </Button>
              </>
            )}
          </div>
        )}

        {(rev.links || []).length > 0 && (
          <>
            <p className="mt-3 font-semibold text-slate-700">Created from this return</p>
            <ul className="mt-1 space-y-0.5" data-testid="ops-revision-links">
              {rev.links.map((l, i) => (
                <li key={i} className="flex items-center gap-1 text-slate-600"><Link2 className="h-3 w-3" /> {l.kind}: {l.number} <span className="text-slate-400">· {d10(l.at)}</span></li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
